import type { Permission } from "@pax/api-contract/common";
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { orpc } from "@/lib/api";
import type { Session } from "./queries";

/** The staff permissions the signed-in person holds, and whether they are an admin. */
interface MyPermissions {
  permissions: readonly Permission[];
  isAdmin: boolean;
}

/** Only accounts hold permissions, so guests and signed-out visitors never ask. */
const NO_PERMISSIONS: MyPermissions = { permissions: [], isAdmin: false };

const myPermissionsQuery = (profileId: string) =>
  orpc.profiles.permissions.get.queryOptions({
    input: { params: { profileId } },
    select: (response): MyPermissions => ({
      permissions: response.permissions ?? [],
      isAdmin: response.isAdmin === true,
    }),
    staleTime: 5 * 60_000,
  });

async function readMyPermissions(
  queryClient: QueryClient,
  user: Session["user"],
): Promise<MyPermissions> {
  if (user.isAnonymous) return NO_PERMISSIONS;
  return queryClient.query(myPermissionsQuery(user.id));
}

/**
 * Thrown by `_authenticated`'s guard when the reader lacks what a staff page needs. `RouteError`
 * shows it as a screen saying so, at the address they asked for, so it still works there once they
 * get a role.
 */
export class NoAccess extends Error {
  constructor() {
    super("You don't have access to this page.");
    this.name = "NoAccess";
  }
}

/** For the route guard, which runs outside React: throws `NoAccess` unless they hold one of `anyOf`. */
export async function requireAnyPermission(
  queryClient: QueryClient,
  session: Session,
  anyOf: readonly Permission[],
): Promise<void> {
  const { permissions } = await readMyPermissions(queryClient, session.user);
  if (!anyOf.some(permission => permissions.includes(permission))) throw new NoAccess();
}

/** For components deciding which staff tools to show. Each staff route checks again on its own. */
export function useMyPermissions(user: Session["user"] | undefined): MyPermissions {
  const { data } = useQuery({
    ...myPermissionsQuery(user?.id ?? ""),
    enabled: user !== undefined && !user.isAnonymous,
  });
  return data ?? NO_PERMISSIONS;
}
